import { FormErrorHandlerService } from '@bds/helpers';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormArray } from '@angular/forms';
import { Component, OnInit, Inject, Renderer2 } from '@angular/core';
import { RtCityAlias, RtStateCode } from '@bds/railtrac-models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { BdsStateService } from '@bds/data-access';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogOptionsBase } from '@bds/core';

export class RtCityAliasDialogOptions extends DialogOptionsBase<RtCityAlias | RtCityAlias[]> {
    title: string;
}

@Component({
    templateUrl: './rt-city-alias-dialog.component.html',
    styleUrls: ['./rt-city-alias-dialog.component.scss'],
})
export class RtCityAliasDialogComponent implements OnInit {
    public stateList: RtStateCode[] = [];
    public showSuccess: boolean = false;

    forms: UntypedFormGroup[] = [];
    formArray: UntypedFormArray = new UntypedFormArray([]);

    // bannerMessage: string;

    constructor(
        public dialogRef: MatDialogRef<RtCityAliasDialogComponent, RtCityAlias | RtCityAlias[]>,
        private renderer: Renderer2,
        @Inject(MAT_DIALOG_DATA) public data: RtCityAliasDialogOptions,
        public snackbar: MatSnackBar,
        public errorService: FormErrorHandlerService,
        public stateService: BdsStateService,
    ) {}

    ngOnInit() {
        this.stateService.getLookup().subscribe((scl) => {
            this.stateList.push(...scl);
        });

        if (this.data && this.data.item) {
            if (Array.isArray(this.data.item)) {
                this.data.item.map((m) => this.forms.push(this.createFormGroup(m)));
            } else {
                this.forms.push(this.createFormGroup(this.data.item));
            }
            this.forms.map((m) => this.formArray.push(m));
        }
    }

    createFormGroup(obj?: RtCityAlias) {
        const alias: RtCityAlias = !!obj ? obj : new RtCityAlias();

        const newForm = new UntypedFormGroup({
            aliasCity: new UntypedFormControl(obj.aliasCity, [
                Validators.required,
                Validators.maxLength(25),
            ]),
            aliasState: new UntypedFormControl(obj.aliasState, [
                Validators.required,
                Validators.maxLength(2),
            ]),
            locCity: new UntypedFormControl(obj.locCity, [
                Validators.required,
                Validators.maxLength(9),
            ]),
            locState: new UntypedFormControl(obj.locState, [
                Validators.required,
                Validators.maxLength(2),
            ]),
            ormId: new UntypedFormControl(obj.ormId, [Validators.required, Validators.min(0)]),
        });

        return newForm;
    }

    getErrorMessage(control: UntypedFormControl) {
        let msg = this.errorService.getShortFormError(control);
        if (!msg) {
            msg = 'Unable to save';
        }

        return msg;
    }

    processConcreteResults(action, result) {
        if (typeof action === 'boolean') {
            if (action) {
                this.dialogRef.close(result);
            }
        } else if (typeof action === 'string') {
            console.warn('Not yet sure how to process (string) results = ', result, action);
            // Not yet used for anything
        } else if (Array.isArray(action)) {
            console.warn('Not yet sure how to process (Array) results = ', result, action);

            const allTrue = action.reduce((pv, cv, idx) => pv === true && cv === true, true);
            if (allTrue === true) {
                this.snackbar.open(
                    `${result.length > 1 ? `${result.length} a` : 'A'}lias${
                        result.length > 1 ? `es` : ''
                    } saved!`,
                    'OK',
                    { duration: 3000 },
                );
                this.dialogRef.close(result);
            }

            for (let idx: number = 0; idx < action.length; idx++) {
                if (action[idx] !== true) {
                    this.forms[idx].setErrors({ apiFault: action[idx] });
                }
            }
            // Array is a string of information
            // Could also be array of objects
        } else {
            console.warn('Not yet sure how to process (any) results = ', result, action);
            // Assume it's a model state dictionary
        }
    }

    onSaveAndCloseClick() {
        console.log('save and close', this.formArray.value);
        if (this.formArray.valid) {
            let result: RtCityAlias | RtCityAlias[];
            if (this.forms.length === 1) {
                result = RtCityAlias.from(this.forms[0].value);
            } else {
                result = this.forms.map((m) => RtCityAlias.from(m.value));
            }
            if (!!this.data && !!this.data.beforeCloseAction) {
                const action = this.data.beforeCloseAction(result);
                if (action instanceof Observable) {
                    action.subscribe((actResult) => {
                        this.processConcreteResults(actResult, result);
                    });
                } else {
                    this.processConcreteResults(action, result);
                }
            }
        }
    }

    resetForm() {
        this.formArray.clear();
        this.forms.splice(0, this.forms.length, this.createFormGroup());
        this.formArray.push(this.forms[0]);
    }
    onSaveAndNewClick() {
        console.log('save and new', this.formArray.value);
        if (this.formArray.valid) {
            let result: RtCityAlias;
            result = RtCityAlias.from(this.forms[0].value);
            if (!!this.data && !!this.data.beforeCloseAction) {
                const action = this.data.beforeCloseAction(result);
                if (typeof action === 'boolean') {
                    if (action) {
                        this.snackbar.open('Alias saved!', 'OK', { duration: 3000 });
                        this.showSaved();
                        this.resetForm();
                        this.focusFirstField();
                    } else {
                    }
                } else if (typeof action === 'string') {
                } else {
                    action.subscribe((actResult) => {
                        if (typeof actResult === 'boolean') {
                            if (actResult) {
                                this.snackbar.open('Alias saved!', 'OK', { duration: 3000 });
                                this.showSaved();
                                this.resetForm();
                                this.focusFirstField();
                            }
                        }
                    });
                }
            }
        }
    }

    focusFirstField() {
        const firstField: HTMLInputElement = this.renderer.selectRootElement('#firstField');
        firstField.focus();
    }
    showSaved(duration: number = 3000) {
        this.showSuccess = true;
        setTimeout(() => {
            this.showSuccess = false;
        }, duration);
    }
    onNoClick() {
        this.dialogRef.close();
    }
}
